/* eslint-disable unicorn/no-useless-promise-resolve-reject */
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { endpoints } from '@/API/endpoints';
import { useApi } from '@/hooks/useApi/useApi';
import { useStateRequest } from '@/hooks/useStateRequest/useStateRequest';
import { IEmployeeGrade, IEmployeePosition,IEmployeePositionGrade } from '@/types';

export interface IGradeAndPositions extends IEmployeeGrade {
	positions: IEmployeePositionGrade[];
}

const QualificationFormUserLevelContext = createContext<{
	isLoadingPositions: boolean;
	setLoadingPositions: (value: boolean) => void;
	positions: IEmployeePosition[];
	setPositions: React.Dispatch<React.SetStateAction<IEmployeePosition[]>>;
	grades: IEmployeeGrade[];
	setGrades: React.Dispatch<React.SetStateAction<IEmployeeGrade[]>>;
	isLoadingPositionGrades: boolean;
	setLoadingPositionGrades: (value: boolean) => void;
	positionGrades: IEmployeePositionGrade[];
	setPositionGrades: React.Dispatch<React.SetStateAction<IEmployeePositionGrade[]>>;
	isLoadingGrades: boolean;
	setLoadingGrades: (value: boolean) => void;
	getGrades: (search?: URLSearchParams, position?: string) => Promise<IEmployeeGrade[]>;
	getPositionGrades: () => Promise<IEmployeePositionGrade[]>;
	getPositions: (
		search?: URLSearchParams,
		category?: string | number
	) => Promise<IEmployeePosition[]>;
} | null>(null);

export const QualificationFormUserLevelContextProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const {
		dataRequest: positions,
		setDataRequest: setPositions,
		loading: isLoadingPositions,
		setLoading: setLoadingPositions,
	} = useStateRequest<IEmployeePosition[]>({ initData: [] });
	const {
		dataRequest: positionGrades,
		setDataRequest: setPositionGrades,
		loading: isLoadingPositionGrades,
		setLoading: setLoadingPositionGrades,
	} = useStateRequest<IEmployeePositionGrade[]>({ initData: [] });

	const {
		dataRequest: grades,
		setDataRequest: setGrades,
		loading: isLoadingGrades,
		setLoading: setLoadingGrades,
	} = useStateRequest<IEmployeeGrade[]>({ initData: [] });

	const { apiGet } = useApi();

	const getPositionGrades = async (): Promise<IEmployeePositionGrade[]> => {
		try {
			const response = await apiGet(`${endpoints.employeePositionGrade}`);
			return response.data.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const getGrades = async (search?: URLSearchParams): Promise<IEmployeeGrade[]> => {
		try {
			const response = await apiGet(
				`${import.meta.env.VITE_API_URL}api/employee/grade/`,
				search
			);
			return response.data.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const getPositions = async (
		search?: URLSearchParams,
		category?: string | number
	): Promise<IEmployeePosition[]> => {
		try {
			const response = await apiGet(
				`${endpoints.employeePosition}${category ? '?category=' + category : ''}`,
				search
			);
			return response.data.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	useEffect(() => {
		setLoadingPositions(true);
		setLoadingGrades(true);
		setLoadingPositionGrades(true);
		Promise.all([getPositions(), getGrades(), getPositionGrades()])
			.then((res) => {
				const [_positions, _grades, _positioGrades] = res;
				setPositions(_positions);
				setGrades(_grades);
				setPositionGrades(_positioGrades);
			})
			.finally(() => {
				setLoadingPositions(false);
				setLoadingGrades(false);
				setLoadingPositionGrades(false);
			});
	}, []);

	return (
		<QualificationFormUserLevelContext.Provider
			value={{
				positions,
				setPositions,
				isLoadingPositions,
				setLoadingPositions,
				grades,
				setGrades,
				isLoadingGrades,
				setLoadingGrades,
				getGrades,
				getPositions,
				positionGrades,
				setPositionGrades,
				isLoadingPositionGrades,
				setLoadingPositionGrades,
				getPositionGrades,
			}}
		>
			{children}
		</QualificationFormUserLevelContext.Provider>
	);
};

export const useQualificationFormUserLevelLogic = () => {
	const context = useContext(QualificationFormUserLevelContext);
	const init = useRef<boolean>(false);
	const [activePosition, setActivePosition] = useState<null | IEmployeePosition>(null);
	const { apiDelete, apiPatch, apiPost } = useApi();

	if (!context) {
		throw new Error('QualificationFormUserLevelContext was not provided');
	}

	const {
		isLoadingPositions,
		isLoadingGrades,
		positionGrades,
		setLoadingPositions,
		setLoadingGrades,
		setPositions,
		setPositionGrades,
		isLoadingPositionGrades,
		setGrades,
	} = context;

	const setLoadingAll = (value: boolean) => {
		setLoadingGrades(value);
		setLoadingPositions(value);
	};

	const onDeletePosition = async (value: IEmployeePosition) => {
		try {
			await apiDelete(`${endpoints.employeePosition}${value.id}/`);

			setPositions((previous) => previous.filter((item) => item.id !== value.id));

			return true;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onDeletePositionGrade = async (value: IEmployeePositionGrade) => {
		try {
			await apiDelete(`${endpoints.employeePositionGrade}/${value.id}/`);

			setPositionGrades((previous) => previous.filter((item) => item.id !== value.id));

			return true;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onUpdatePosition = async (value: {
		title: string;
		link: string;
		data: IEmployeePosition;
	}): Promise<IEmployeePosition> => {
		const updatePosition = (list: IEmployeePosition[]) => {
			const array = [...list];
			for (const element of array) {
				if (element.id === value.data.id) {
					element.responsibilities_link = value.link;
					element.name = value.title;
					break;
				}
			}
			return array;
		};

		try {
			const res = await apiPatch(`${endpoints.employeePosition}/${value.data.id}/`, {
				responsibilities_link: value.link || '',
				name: value.title,
			});

			setPositions((positions) => updatePosition(positions));

			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onUpdateGrade = async (value: {
		name: string;
		level?: number;
		id: number;
	}): Promise<IEmployeeGrade> => {
		const updateGrade = (list: IEmployeeGrade[]) => {
			const array = [...list];
			for (const element of array) {
				if (element.id === value.id) {
					element.name = value.name;
					break;
				}
			}
			return array;
		};
		try {
			const response = await apiPatch(
				`${import.meta.env.VITE_API_URL}api/employee/grade/${value.id}/`,
				{
					name: value.name,
				}
			);

			setGrades((grades) => updateGrade(grades));

			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onUpdatePositionGrade = async (
		id: number,
		value: {
			name?: string;
			education_link: string;
			possitionGrtadeId: number;
			gradeId: number;
		}
	): Promise<IEmployeePositionGrade> => {
		const updatePositionGrade = (list: IEmployeePositionGrade[]) => {
			const array = [...list];
			for (const element of array) {
				if (element.id === id) {
					element.education_link = value.education_link;
					// arr[i].position = value.possitionGrtadeId;
					// arr[i].grade = value.gradeId;
					break;
				}
			}
			return array;
		};

		try {
			if (value.name) {
				await onUpdateGrade({ name: value.name, id: value.gradeId });
			}

			const response = await apiPatch(`${endpoints.employeePositionGrade}/${id}/`, {
				education_link: value.education_link,
				grade: value.gradeId,
				position: value.possitionGrtadeId,
			});

			setPositionGrades((positionGrades) => updatePositionGrade(positionGrades));

			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onCreateGrade = async (values: {
		name: string;
		level?: number;
	}): Promise<IEmployeeGrade> => {
		try {
			const response = await apiPost(`${import.meta.env.VITE_API_URL}api/employee/grade/`, {
				name: values.name,
			});
			setGrades((previous) => [response.data, ...previous]);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onCreatePositionGrade = async (
		values: {
			position: number;
			name: string;
			education_link?: string;
		},
		gradeСonnectionID?: number
	): Promise<IEmployeePositionGrade> => {
		try {
			let gradeID = gradeСonnectionID;

			if (gradeСonnectionID === undefined) {
				// eslint-disable-next-line unicorn/no-await-expression-member
				gradeID = (await onCreateGrade({ name: values.name })).id;
			}

			const response = await apiPost(`${endpoints.employeePositionGrade}/`, {
				name: values.name,
				grade: gradeID,
				position: values.position,
				education_link: values?.education_link || '',
			});
			setPositionGrades((previous) => [response.data, ...previous]);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const onCreatePosition = async (values: {
		name: string;
		responsibilities_link?: string;
	}): Promise<IEmployeePosition> => {
		try {
			const response = await apiPost(`${endpoints.employeePosition}`, {
				name: values.name,
				responsibilities_link: values?.responsibilities_link || '',
			});

			setPositions((previous) => [response.data, ...previous]);

			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	useEffect(() => {
		init.current = true;
	}, []);

	const isLoadingAll = useMemo(() => {
		return isLoadingPositions || isLoadingGrades || isLoadingPositionGrades;
	}, [isLoadingPositions, isLoadingGrades, isLoadingPositionGrades]);

	const gradesFiltredPosition = useMemo(() => {
		return activePosition === null
			? context.positionGrades
			: context.positionGrades.filter((item) => item.position === activePosition.id);
	}, [activePosition, context.positions, context.grades, context.positionGrades]);

	const gradesTitleKeys = useMemo(() => {
		const titles: { [key: number]: string } = {};
		for (const item of context.grades) {
			titles[item.id] = item.name;
		}
		return titles;
	}, [context.positionGrades, context.grades]);

	const gradesAdvanced = useMemo(() => {
		const _grades: IGradeAndPositions[] = [];
		for (const item of context.grades) {
			const positions = positionGrades.filter((position) => position.grade === item.id);
			_grades.push({
				...item,
				positions: positions,
			});
		}
		return _grades;
	}, [context.positionGrades, context.grades, positionGrades]);

	const gradesAdvancedPositionActive = useMemo(() => {
		return activePosition === null
			? gradesAdvanced
			: gradesAdvanced.filter(
					(item) =>
						// eslint-disable-next-line unicorn/prefer-array-some
						!!item.positions.find((pos) => pos.position === activePosition.id)
				);
	}, [activePosition, context.positions, context.grades, gradesAdvanced, context.positionGrades]);

	return {
		...context,
		isLoadingAll,
		setLoadingAll,
		onDeletePosition,
		onDeletePositionGrade,
		onCreatePosition,
		onCreatePositionGrade,
		activePosition,
		setActivePosition,
		onUpdateGrade,
		onUpdatePosition,
		gradesFiltredPosition,
		gradesTitleKeys,
		gradesAdvanced,
		gradesAdvancedPositionActive,
		onCreateGrade,
		onUpdatePositionGrade,
		//onGetCompetencesByCategory,
		//onSetCategoryOfCompetence,
	};
};
